/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Control plane API endpoints.
 *
 * Mirrors `backend/control/urls.py`:
 *   - GET /api/v1/control/tenants/        list paginated tenants
 *   - GET /api/v1/control/tenants/:id/    one tenant in detail
 *
 * Read-only in Stage 1. Mutations (provision, suspend, manifest edit) land
 * later. Access is restricted to user_type='control' server-side.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";

import type { ControlTenant, TenantStatus } from "../model/types";

interface ControlTenantListParams {
  page?: number;
  page_size?: number;
  status?: TenantStatus;
}

/** RTK Query endpoints for the TNN-staff control plane. */
export const controlApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * GET /api/v1/control/tenants/
     *
     * Default page size is 10 per StandardPagination; for the cohort view
     * we ask for 50 so all 26 pilot tenants land on one page (avoid having
     * to paginate the table). The backend caps higher requests; verify
     * if/when the cohort grows past ~100.
     */
    listControlTenants: build.query<Paginated<ControlTenant>, ControlTenantListParams>({
      query: (params) => ({
        url: "/control/tenants/",
        params: { page_size: 50, ...params } as Record<string, string | number>,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "ControlTenant" as const,
                id,
              })),
              { type: "ControlTenant", id: "LIST" },
            ]
          : [{ type: "ControlTenant", id: "LIST" }],
    }),

    getControlTenant: build.query<ControlTenant, number>({
      query: (id) => `/control/tenants/${id}/`,
      providesTags: (_, __, id) => [{ type: "ControlTenant", id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListControlTenantsQuery,
  useGetControlTenantQuery,
} = controlApi;
